import { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { TAB_HEIGHT } from '@/features/canvas/constants';
import { FilteredNodes } from '@/features/canvas/contexts/editor-context';

// Constants matching other Gantt components exactly
const GANTT_PADDING_ABOVE = 6;
// Variable heights for parent vs child rows (matching other columns)
const GANTT_PARENT_HEIGHT = 28; // Height for parent rows
const GANTT_CHILD_HEIGHT = 20; // Height for child rows (more compact)
const GANTT_SECTION_PADDING = GANTT_PADDING_ABOVE;
// Swimlane minimum height to match other Gantt components
const SWIMLANE_MIN_HEIGHT = 97.06;

// Helper function to get row height based on depth (matching other columns)
const getRowHeight = (depth: number) => (depth > 0 ? GANTT_CHILD_HEIGHT : GANTT_PARENT_HEIGHT);

type Props = {
  isOdd: boolean;
  onHeightChange: (sectionId: number, height: number) => void;
  sectionId: number;
  treeItems: { depth: number; node: FilteredNodes }[];
  title?: string;
};

const GanttSidebarSection = ({ title, isOdd, sectionId, onHeightChange, treeItems }: Props) => {
  const childRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (childRef.current && parentRef.current) {
      const childHeight = childRef.current.getBoundingClientRect().height;

      // Calculate height using variable row heights (exactly matching other columns)
      const calculatedHeight =
        treeItems.length > 0
          ? treeItems.reduce((sum, item) => sum + getRowHeight(item.depth), 0) +
            GANTT_SECTION_PADDING
          : SWIMLANE_MIN_HEIGHT;

      const finalHeight = Math.max(childHeight, calculatedHeight, SWIMLANE_MIN_HEIGHT);

      parentRef.current.style.minHeight = `${finalHeight}px`;

      // Notify parent about this section's calculated height
      onHeightChange(sectionId, finalHeight);
    }
  }, [treeItems, title, sectionId, onHeightChange]);

  return (
    <SectionHead $isOdd={isOdd} ref={parentRef}>
      <SectionTitle ref={childRef}>{title}</SectionTitle>
    </SectionHead>
  );
};

const SectionHead = styled.div<{ $isOdd: boolean }>`
  position: relative;
  min-height: 97.06px;
  width: ${TAB_HEIGHT}px;
  background-color: ${({ theme, $isOdd }) =>
    $isOdd ? theme.palette.brand.editorTabPrimary : theme.palette.brand.editorTabSecondary};
  border-radius: 1rem 0 0 1rem;
`;

const SectionTitle = styled.div`
  text-align: center;
  position: absolute;
  top: 50%;
  left: 50%;
  color: ${({ theme }) => theme.palette.brand.white};
  font-weight: 900;
  font-size: 16px;
  text-wrap: nowrap;
  transform: translateX(-50%) translateY(-50%) rotate(-90deg);
  padding: 17px;
  white-space: nowrap;
  text-shadow: 0px 0px 1px ${({ theme }) => theme.palette.brand.white};
`;

export { GanttSidebarSection };
